import { API, get, put, remove, FilterFields } from "api";
import { OK } from "http-status-codes";
import { Template } from "./templates";
import { Filter } from "api";

export interface SchedulerExceptions {
    data: Exception[];
}

export interface SchedulerConfiguration {
    data: SchedulerRule[];
}

export interface SchedulerRuleFields {
    data: SchedulerRuleDefinition;
}

export interface SchedulerRuleDefinition {
    fields: FilterFields[];
    templates: Template[];
    deadlines: Array<{
        name: string;
        translation: string;
    }>;
    statuses: Array<{
        code: number;
        translation: string;
    }>;
}

export interface Exception {
    name: string;
    behaviour: {
        type?: string;
        day?: string;
        time?: string;
        template?: string;
        lookaheaddays?: number;
        status?: number;
    };
    filter: Filter[];
}

export interface SchedulerRule {
    id?: string;
    name: string;
    weekends: boolean;
    enabled: boolean;
    hql?: string;
    filter?: Filter[];
    behaviour: Behaviour;
    exceptions?: string[];
}

export interface Behaviour {
    type?: string;
    status?: number;
    component?: string;
    initialstatuses?: string;
    deadline: string;
    order: boolean;
    secondsbeforenotificationstartformanualemail?: number;
    time2deadlines?: TimeDeadlines[];
}

interface TimeDeadlines {
    secondsbeforedeadline: number;
    days: number;
    hours: number;
    minutes: number;
    template?: string;
    status?: number;
}

/**
 * Get list of created configurations
 * @param headers
 */
const getSchedulerConfiguration = async (headers: HeadersInit): Promise<SchedulerConfiguration | undefined> => {
    const { parsedBody } = await get<SchedulerConfiguration>(`${API.baseApiUrl}/notification/rules`, {
        headers,
    });

    return parsedBody;
};

/**
 * Delete rule
 * @param id Rule id
 * @param headers
 */
const deleteSchedulerRule = async (id: string, headers: HeadersInit): Promise<number> => {
    await remove(`${API.baseApiUrl}/notification/rule?id=${id.trim()}`, {
        headers,
    });

    return OK;
};

/**
 * Get form fields
 * @param headers
 */
const getSchedulerFilterFields = async (headers: HeadersInit): Promise<SchedulerRuleDefinition | undefined> => {
    const { parsedBody } = await get<SchedulerRuleFields>(`${API.baseApiUrl}/notification/fields`, {
        headers,
    });

    return parsedBody?.data;
};

/**
 * Get list of created exceptions
 * @param headers
 */
const getSchedulerRuleExceptions = async (headers: HeadersInit): Promise<Exception[]> => {
    const { parsedBody } = await get<SchedulerExceptions>(`${API.baseApiUrl}/notification/exceptions`, {
        headers,
    });

    return parsedBody?.data || [];
};

/**
 * Create new rule
 * @param body
 * @param headers
 */
const createSchedulerRule = async (body: SchedulerRule, headers: HeadersInit): Promise<number> => {
    await put(`${API.baseApiUrl}/notification/rules`, body, {
        headers,
    });

    return OK;
};

/**
 * Create new rule
 * @param body
 * @param id
 * @param headers
 */
const updateSchedulerRule = async (body: SchedulerRule, headers: HeadersInit, id?: string): Promise<number> => {
    await put(`${API.baseApiUrl}/notification/rules?id=${id?.trim()}`, body, {
        headers,
    });

    return OK;
};

/**
 * Delete specific exception
 * @param name
 * @param headers
 */
const deleteExceptionRule = async (name: string, headers: HeadersInit): Promise<number> => {
    await remove(`${API.baseApiUrl}/notification/exception?name=${name.trim()}`, {
        headers,
    });

    return OK;
};

/**
 * Create new exception
 * @param body
 * @param headers
 */
const createSchedulerRuleException = async (body: Exception, headers: HeadersInit): Promise<number> => {
    await put<Exception>(`${API.baseApiUrl}/notification/exception`, body, {
        headers,
    });

    return OK;
};

/**
 * Update exception
 * @param body
 * @param headers
 */
const updateSchedulerRuleException = async (body: Exception, headers: HeadersInit): Promise<number> => {
    await put<Exception>(`${API.baseApiUrl}/notification/exception`, body, {
        headers,
    });

    return OK;
};

export default {
    getSchedulerRuleExceptions,
    getSchedulerConfiguration,
    getSchedulerFilterFields,
    createSchedulerRule,
    updateSchedulerRule,
    createSchedulerRuleException,
    updateSchedulerRuleException,
    deleteSchedulerRule,
    deleteExceptionRule,
};
